import React, { useEffect, useState, useRef } from 'react';
import {
  BubbleInput,
  BubbleAlert,
  BubbleButton,
  BubbleTextArea,
  BubbleFormGroup,
  BubbleOverlay,
  BubbleBadge,
  BubbleDebounce,
} from '@go-client/components/BubbleUI';
import { safeIdCompare, hasOwnProperty } from '@go-shared/lib/utils';
import { Popup } from '@spark-labs/avocado-ui';
import { EventTemplate } from '@go-shared/types/eventTemplate';
import { paginateEventTemplates } from '@go-client/lib/api/eventTemplate';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import {
  EventTemplateTransforms,
  EventTemplateTranslatable,
  AutoResolveConflictStrategy,
  EventTemplateTranslation,
} from '@go-shared/types/translation';
import {
  replaceTranslationByRefer,
  exportTranslationsToCSV,
  importTranslationsFromCSV,
  resolveTranslationConflicts,
} from '@go-client/lib/api/translation';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { RouteComponentProps, Prompt } from 'react-router-dom';
import Translations from './Translations';
import TranslationBlock from '@go-client/components/TranslationBlock';
import {
  CommonTranslationFilters,
  generateDefaultNewTranslation,
  makeEventTranslationTransform,
  TranslationFilters,
} from '@go-shared/lib/translation';
import { UploadButton } from '@go-client/components/UIkit/forms/UploadButton/UploadButton';

import { getErrorMessage } from '@go-shared/errors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TranslationsSubNavigation from '../../components/Navigations/TranslationsSubNavigation';
import { getMultiPropertyKind } from '@go-shared/lib/libraries';
import {
  canModifyMultiPropertyItem,
  canManageItemPermissionLevel,
} from '@go-shared/lib/permissions';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { DEFAULT_PER_PAGE, PaginationMetadata } from '@go-shared/types/pagination';
import { PaginateTranslations } from '@go-client/components/Translations/PaginateTranslations';

interface EventLibraryTranslationDataBundle {
  eventTemplates: EventTemplate[];
}

interface EventLibraryTranslationForm {
  dirty: boolean;
  transforms: EventTemplateTransforms;
}

interface ChangeEventTemplateFormAction {
  active?: boolean;
  valueChange?: { key: keyof EventTemplateTranslatable; value: string };
}

type EventLibraryTranslationsPageProps = RouteComponentProps;

export default function EventLibraryTranslations(props: EventLibraryTranslationsPageProps) {
  const { user } = useUserInfo();
  const { activeProperty } = useActiveProperty();
  const serverInfo = useServerInfo();
  const { languages } = serverInfo;

  const [loading, setLoading] = useState(true);
  const [loadingTranslations, setLoadingTranslations] = useState(true);
  const [dataBundle, setDataBundle] = useState<EventLibraryTranslationDataBundle | undefined>(
    undefined
  );
  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>(
    undefined
  );

  const [activeEvtTmpl, setActiveEvtTmpl] = useState<EventTemplate | undefined>(undefined);
  const [evtTmplTranslForm, setEvtTmplTranslForm] = useState<
    EventLibraryTranslationForm | undefined
  >(undefined);

  const [showImportExportCSV, setShowImportExportCSV] = useState<{
    show: boolean;
    mode: 'file' | 'conflict';
    loading: boolean;
    importSuccess: boolean | undefined;
    errorMsg: string | undefined;
    conflicts: { location: string; count: number } | undefined;
  }>({
    show: false,
    mode: 'file',
    loading: false,
    importSuccess: undefined,
    errorMsg: undefined,
    conflicts: undefined,
  });

  const [paginationFilters, setPaginationFilters] = useState<CommonTranslationFilters>({
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    createdFrom: '',
    createdTo: '',
    propertyId: activeProperty._id,
    translated: undefined,
    published: undefined,
  });

  const contentRef = useRef<HTMLDivElement | null>(null);
  // this will scroll the page to top when search is modified
  useEffect(() => {
    // We want to scroll to top only when we are in page 1 or paginationParams are reset
    if (paginationFilters.page !== 1) {
      return;
    }

    if (contentRef && contentRef.current) contentRef.current.scrollTop = 0;
  }, [paginationFilters, contentRef]);

  useEffect(() => {
    if (activeProperty._id !== paginationFilters.propertyId) {
      setPaginationFilters({ ...paginationFilters, propertyId: activeProperty._id });
    }
  }, [activeProperty]);

  async function refresh() {
    setLoadingTranslations(true);
    await fetchDataBundle();
  }

  // this will refresh the currently active venue template if data bundle refreshes
  useEffect(() => {
    if (!dataBundle) return;
    setActiveEvtTmpl(state => {
      const defaultEvtTmpl = dataBundle.eventTemplates[0];
      if (!state) return defaultEvtTmpl;
      const refreshedEvtTmpl = dataBundle.eventTemplates.find(et => et._id === state._id);
      return refreshedEvtTmpl || defaultEvtTmpl;
    });
  }, [dataBundle]);

  useEffect(() => {
    refresh();
  }, [paginationFilters]);

  // this will reset the form each time a new event template is clicked
  useEffect(() => {
    resetEvtTmplForm();
  }, [activeEvtTmpl]);

  async function fetchDataBundle() {
    try {
      const { meta, data } = await paginateEventTemplates({
        propertyId: activeProperty._id,
        page: paginationFilters.page,
        translations: true,
        ...(paginationFilters.search && { search: paginationFilters.search }),
        ...(paginationFilters.createdFrom && { startDate: paginationFilters.createdFrom }),
        ...(paginationFilters.createdTo && { endDate: paginationFilters.createdTo }),
        ...(paginationFilters?.translated !== undefined && {
          translated: paginationFilters.translated,
        }),
        ...(paginationFilters?.published !== undefined && {
          published: paginationFilters.published,
        }),
      });

      setPaginationMetadata(meta);

      const eventTemplates =
        paginationFilters.page > 1 ? [...(dataBundle?.eventTemplates ?? []), ...data] : data;

      setDataBundle({
        eventTemplates,
      });
    } finally {
      setLoading(false);
      setLoadingTranslations(false);
    }
  }

  function resetEvtTmplForm() {
    const transforms = activeEvtTmpl
      ? makeEventTranslationTransform(
          languages.map(l => l.key),
          activeEvtTmpl?.translations
        )
      : undefined;

    if (!transforms) {
      setEvtTmplTranslForm(undefined);
      return;
    }

    const formData: EventLibraryTranslationForm = {
      dirty: false,
      transforms,
    };

    setEvtTmplTranslForm(formData);
  }

  function changeEvtTmplForm(lang: string, change: ChangeEventTemplateFormAction) {
    if (!evtTmplTranslForm) return;

    const updatedForm = { ...evtTmplTranslForm, dirty: true };

    if (!hasOwnProperty(updatedForm.transforms, lang)) {
      return;
    }

    updatedForm.transforms[lang].modified = true;

    if (change.active !== undefined) updatedForm.transforms[lang].active = change.active;
    if (change.valueChange !== undefined) {
      updatedForm.transforms[lang].delta[change.valueChange.key] = change.valueChange.value;
    }

    setEvtTmplTranslForm(updatedForm);
  }

  async function handleSubmitEvtTmplForm() {
    if (
      !evtTmplTranslForm ||
      !activeEvtTmpl ||
      !dataBundle ||
      !canModifyMultiPropertyItem(user, serverInfo, activeProperty._id, activeEvtTmpl)
    )
      return;

    const translation = generateDefaultNewTranslation(activeEvtTmpl._id, 'eventtemplate');

    translation.transforms = evtTmplTranslForm.transforms;

    const response = await replaceTranslationByRefer(activeEvtTmpl._id, translation);
    const active = { ...activeEvtTmpl, translations: response as EventTemplateTranslation };
    const idx = dataBundle.eventTemplates.findIndex(vt => vt._id === active._id);
    dataBundle.eventTemplates[idx] = active;
    // No need to refresh bundle, just update the existing list
    setActiveEvtTmpl(active);
    setDataBundle({ ...dataBundle });
  }

  async function generateCSV(filters: TranslationFilters) {
    const result = await exportTranslationsToCSV(activeProperty._id, 'eventtemplate', {
      ...filters,
      selectedLanguages: filters.selectedLanguages.map(l => l.key),
    });
    if (!result.location) return;
    window.open(result.location, '_blank');
  }

  async function uploadTranslationsFile(file: File) {
    setShowImportExportCSV(state => ({ ...state, loading: true }));

    try {
      const conflicts = await importTranslationsFromCSV(activeProperty._id, 'eventtemplate', file);
      const mode = conflicts.count > 0 ? 'conflict' : 'file';

      setShowImportExportCSV(state => ({
        ...state,
        mode,
        conflicts,
        importSuccess: true,
      }));
    } catch (err) {
      setShowImportExportCSV(state => ({
        ...state,
        importSuccess: false,
        errorMsg: getErrorMessage(err),
      }));
      throw err;
    } finally {
      setShowImportExportCSV(state => ({ ...state, loading: false }));
      await fetchDataBundle();
    }
  }

  async function resolveConflicts(path: string, strategy?: AutoResolveConflictStrategy) {
    setShowImportExportCSV(state => ({ ...state, loading: true }));

    try {
      if (strategy) await resolveTranslationConflicts(activeProperty._id, { path, strategy });
      setShowImportExportCSV(state => ({ ...state, importSuccess: true }));
    } catch (err) {
      setShowImportExportCSV(state => ({ ...state, importSuccess: false }));
      throw err;
    } finally {
      setShowImportExportCSV(state => ({
        ...state,
        conflicts: undefined,
        mode: 'file',
        loading: false,
      }));
      await fetchDataBundle();
    }
  }

  function headerFn() {
    return <TranslationsSubNavigation active="events" property={activeProperty} />;
  }

  function leftFn() {
    const eventTemplatesWithTranslations = dataBundle?.eventTemplates || [];

    const loadMoreTranslations =
      paginationMetadata && paginationMetadata.current_page < paginationMetadata.last_page;

    return (
      <>
        <div
          className="sticky flex bg-white border-b"
          style={{ top: 'calc(130px + 40px + 0.75rem)', height: '50px', zIndex: 3 }}
        >
          <BubbleDebounce
            onTrigger={[
              (search: string) => setPaginationFilters({ ...paginationFilters, page: 1, search }),
              500,
            ]}
          >
            {({ debounce }) => (
              <BubbleInput
                type="text"
                block
                appearance="bare"
                placeholder="Search..."
                className="flex-grow"
                inputStyle={{ height: '49px' }}
                inputClassName="px-5"
                value={paginationFilters.search}
                onValue={debounce}
              />
            )}
          </BubbleDebounce>
        </div>
        <div style={{ paddingTop: '50px' }}>
          {eventTemplatesWithTranslations.map(etmpl => {
            const evtStatus = getMultiPropertyKind(etmpl);

            return (
              <div
                key={etmpl._id}
                className={`list-item px-5 py-3 cursor-pointer ${
                  activeEvtTmpl && safeIdCompare(activeEvtTmpl._id, etmpl._id) ? 'active' : ''
                }`}
                onClick={() => setActiveEvtTmpl(etmpl)}
              >
                <div className="flex items-center">
                  <div className="flex-grow min-w-0">
                    <BubbleBadge variant={evtStatus.variant}>{evtStatus.status}</BubbleBadge>
                    <div className="list-item-name font-semibold overflow-ellipsis">
                      {etmpl.title}
                    </div>
                    <div className="text-slate-dark overflow-ellipsis">{etmpl.description}</div>
                  </div>
                  <FontAwesomeIcon
                    icon="chevron-right"
                    className="list-item-arrow ml-auto pl-3 flex-shrink-0"
                  />
                </div>
              </div>
            );
          })}

          {eventTemplatesWithTranslations.length < 1 && (
            <div className="grid items-center justify-center italic text-smoke bg-eggshell px-3 py-2">
              No event templates found
            </div>
          )}

          {loadingTranslations && (
            <BubbleOverlay
              loading
              hideBg
              style={{ marginTop: 'calc(40px + 0.75rem)' }}
              loadingMinHeight="medium"
              className="shadow rounded"
            />
          )}

          {loadMoreTranslations && (
            <PaginateTranslations
              type="Events"
              onLoadMoreClick={() =>
                setPaginationFilters({
                  ...paginationFilters,
                  page: paginationFilters.page + 1,
                })
              }
            />
          )}
        </div>
      </>
    );
  }

  function rightFn({ selectedLanguages }: TranslationFilters) {
    return (
      <>
        {dataBundle && activeEvtTmpl && evtTmplTranslForm && (
          <>
            <div style={{ marginBottom: '-1px' }}>
              {!canModifyMultiPropertyItem(user, serverInfo, activeProperty._id, activeEvtTmpl) && (
                <BubbleAlert className="mt-3 mx-3" variant="ocean">
                  This event cannot be modified. It may be a brand level event or you lack library
                  permissions.
                </BubbleAlert>
              )}
              <TranslationBlock
                label="Title"
                value={activeEvtTmpl.title}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({evtTmplTranslForm.transforms[language.key].delta.title.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !evtTmplTranslForm.transforms[language.key].active ? 'smoke' : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeEvtTmplForm(language.key, {
                          active: !evtTmplTranslForm.transforms[language.key].active,
                        })
                      }
                      icon={evtTmplTranslForm.transforms[language.key].active ? 'eye' : 'eye-slash'}
                    />
                    <BubbleInput
                      type="text"
                      block
                      className="flex-grow"
                      inputClassName={
                        !evtTmplTranslForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={evtTmplTranslForm.transforms[language.key].delta.title}
                      onValue={val =>
                        changeEvtTmplForm(language.key, {
                          valueChange: { key: 'title', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
              <TranslationBlock
                label="Description"
                value={activeEvtTmpl.description}
                languages={selectedLanguages}
                charCountFn={({ language }) => (
                  <span className="text-smoke-light">
                    ({evtTmplTranslForm.transforms[language.key].delta.description.length})
                  </span>
                )}
              >
                {({ language }) => (
                  <div className="flex items-start">
                    <BubbleButton
                      inverse
                      variant={
                        !evtTmplTranslForm.transforms[language.key].active ? 'smoke' : 'navy'
                      }
                      className="px-1-i pt-2 mr-2 leading-none"
                      onClick={() =>
                        changeEvtTmplForm(language.key, {
                          active: !evtTmplTranslForm.transforms[language.key].active,
                        })
                      }
                      icon={evtTmplTranslForm.transforms[language.key].active ? 'eye' : 'eye-slash'}
                    />
                    <BubbleTextArea
                      block
                      className="flex-grow"
                      textAreaClassName={
                        !evtTmplTranslForm.transforms[language.key].active
                          ? 'italic text-smoke'
                          : ''
                      }
                      value={evtTmplTranslForm.transforms[language.key].delta.description}
                      onValue={val =>
                        changeEvtTmplForm(language.key, {
                          valueChange: { key: 'description', value: val },
                        })
                      }
                    />
                  </div>
                )}
              </TranslationBlock>
            </div>
            <div className="sticky bottom-0 p-3 bg-eggshell border-t">
              <BubbleButton
                onClick={() => handleSubmitEvtTmplForm()}
                variant="navy"
                disabled={
                  !evtTmplTranslForm.dirty ||
                  !canModifyMultiPropertyItem(user, serverInfo, activeProperty._id, activeEvtTmpl)
                }
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="slate"
                disabled={!evtTmplTranslForm.dirty}
                inverse
                onClick={() => resetEvtTmplForm()}
                icon="rotate-left"
              >
                Reset
              </BubbleButton>
            </div>
          </>
        )}
      </>
    );
  }

  function sideFn() {
    return (
      <div className="flex flex-col relative items-stretch p-4 bg-titanium-darker">
        <div className="grid gap-2">
          <BubbleButton
            variant="slate"
            onClick={() =>
              setShowImportExportCSV({
                show: true,
                mode: 'file',
                loading: false,
                importSuccess: undefined,
                errorMsg: undefined,
                conflicts: undefined,
              })
            }
            className="text-left"
            icon="tasks"
          >
            File Operations
          </BubbleButton>
        </div>
      </div>
    );
  }

  function outerFn(filters: TranslationFilters) {
    return (
      <>
        {showImportExportCSV.show && (
          <Popup
            title={showImportExportCSV.mode === 'file' ? 'File Operations' : 'Resolve Conflicts'}
            onClose={() =>
              setShowImportExportCSV({
                show: false,
                mode: 'file',
                loading: false,
                importSuccess: undefined,
                errorMsg: undefined,
                conflicts: undefined,
              })
            }
          >
            <hr />
            {showImportExportCSV.importSuccess === true && (
              <BubbleAlert variant="lime" className="rounded-0-i" dismissible>
                <b>Success! </b> The uploaded file was processed successfully and translations were
                imported.
              </BubbleAlert>
            )}
            {showImportExportCSV.importSuccess === false && (
              <>
                <BubbleAlert
                  variant="crimson"
                  className="rounded-0-i"
                  dismissible
                  onDismiss={() =>
                    setShowImportExportCSV({
                      ...showImportExportCSV,
                      importSuccess: undefined,
                      errorMsg: undefined,
                    })
                  }
                >
                  <b>Error!</b> Could not process the file provided. Please try again, or export a
                  new file and transfer your changes.
                </BubbleAlert>
                {showImportExportCSV.errorMsg && (
                  <div className="bg-eggshell px-5 py-3 border-t border-b border-crimson">
                    <p className="font-semibold mb-3">The following errors occurred:</p>
                    <pre>{showImportExportCSV.errorMsg}</pre>
                  </div>
                )}
              </>
            )}
            {showImportExportCSV.mode === 'file' ? (
              <BubbleOverlay
                loading={showImportExportCSV.loading}
                className="p-4"
                style={{ width: '100vw', maxWidth: '800px' }}
              >
                <div className="mb-5 bg-eggshell p-3 border rounded">
                  <p className="mb-3">
                    Use this panel to import/export a file of event templates and their
                    translations.<b>Importing can only be done in brand instances.</b>
                  </p>
                  <p className="mb-3">
                    <b>CAUTION:</b> Modifying existing translations in the file may cause conflicts.
                    You will be prompted to resolve these conflicts (if any) on the next screen.
                  </p>
                  <p>
                    Only modify the <i>active</i> or <i>translated</i> columns of the file.
                  </p>
                </div>
                <div className="flex space-x-4">
                  <BubbleButton block onClick={() => generateCSV(filters)} variant="navy">
                    Download Translations
                  </BubbleButton>
                  {canManageItemPermissionLevel(user, serverInfo) && (
                    <div className="w-full">
                      <UploadButton
                        accept=".csv, .xlsx"
                        singleUpload
                        onFilesChange={async files => uploadTranslationsFile(files[0])}
                        render={upload => {
                          return (
                            <BubbleButton variant="slate" block onClick={upload}>
                              Upload Translations
                            </BubbleButton>
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
              </BubbleOverlay>
            ) : (
              <BubbleOverlay
                loading={showImportExportCSV.loading}
                className="p-4"
                style={{ width: '100vw', maxWidth: '800px' }}
              >
                {showImportExportCSV.conflicts && (
                  <>
                    <p className="mb-5 bg-eggshell p-3 border rounded">
                      There were{' '}
                      <span className="text-banana-darker">
                        <b>{showImportExportCSV.conflicts.count} conflict(s)</b>
                      </span>{' '}
                      in your file. Resolve these conflicts with the below options.
                    </p>
                    <BubbleFormGroup label="Step 1: View Conflicts" className="mb-5">
                      <BubbleButton
                        variant="navy"
                        block
                        onClick={() =>
                          showImportExportCSV.conflicts &&
                          showImportExportCSV.conflicts.location &&
                          window.open(showImportExportCSV.conflicts.location, '_blank')
                        }
                      >
                        Download Conflicts
                      </BubbleButton>
                    </BubbleFormGroup>
                    <BubbleFormGroup label="Step 2: Resolve Conflicts (Choose One)">
                      <div className="flex space-x-4 space-between">
                        <BubbleButton
                          variant="lime"
                          block
                          onClick={() =>
                            showImportExportCSV.conflicts &&
                            showImportExportCSV.conflicts.location &&
                            resolveConflicts(showImportExportCSV.conflicts.location, 'mine')
                          }
                        >
                          Use Translations From File
                        </BubbleButton>
                        <BubbleButton
                          variant="ocean"
                          block
                          onClick={() =>
                            showImportExportCSV.conflicts &&
                            showImportExportCSV.conflicts.location &&
                            resolveConflicts(showImportExportCSV.conflicts.location)
                          }
                        >
                          Use Current Library Translations
                        </BubbleButton>
                        <div className="w-full">
                          <UploadButton
                            accept=".csv"
                            singleUpload
                            onFilesChange={async files => uploadTranslationsFile(files[0])}
                            render={upload => {
                              return (
                                <BubbleButton variant="slate" block onClick={upload}>
                                  <div>Upload Resolution</div>
                                  <div>(New CSV of Resolutions)</div>
                                </BubbleButton>
                              );
                            }}
                          />
                        </div>
                      </div>
                    </BubbleFormGroup>
                  </>
                )}
              </BubbleOverlay>
            )}
          </Popup>
        )}
      </>
    );
  }

  // this adds a check to prevent closing tab if unsaved changes
  useEffect(() => {
    if (evtTmplTranslForm && evtTmplTranslForm.dirty) {
      window.onbeforeunload = () => true;
    } else {
      window.onbeforeunload = null;
    }
  });

  return (
    <>
      <Prompt
        when={evtTmplTranslForm && evtTmplTranslForm.dirty}
        message="You have unsaved changes! Are you sure you want to leave?"
      />
      <Translations
        {...props}
        showLibraryFilters
        category="events"
        loading={loading}
        contentRef={contentRef}
        property={activeProperty}
        languages={languages}
        leftFn={leftFn}
        rightFn={rightFn}
        headerFn={headerFn}
        sideFn={sideFn}
        outerFn={outerFn}
        filters={paginationFilters}
        setFilters={setPaginationFilters}
      />
    </>
  );
}
